import {useEffect, useId, useMemo, useState} from "react";
import type {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {OtsTooltipPanel} from "./OtsTooltip";
import {OtsProgressBar} from "./OtsProgressBar";

export const COSTING_PROGRESS_ATTRIBUTE = "data-ots-costing-progress";
export const COSTING_STARTED_AT_ATTRIBUTE = "data-ots-costing-started-at";
export const COSTING_TOOLTIP_DELAY_MS = 2500;

export type ProgressWork = {
  completed: number;
  total?: number | null;
  unit: string;
  percent?: number | null;
};
export type ProgressOutcomes = {succeeded: number; failed: number; skipped: number};
export type CostingStage = {
  id: string;
  label: string;
  status: "PENDING" | "RUNNING" | "COMPLETE" | "SKIPPED" | "FAILED";
  completedRuns: number;
  work?: ProgressWork | null;
};
export type CostingProgress = {
  state: "RUNNING" | "SUCCEEDED" | "FAILED";
  currentLabel: string;
  startedAt?: number;
  stages: CostingStage[];
  currentStageId?: string | null;
  /** Job-wide counts, when the caller knows the whole job's size. Stage counts stay on their stage. */
  work?: ProgressWork | null;
  updatedAt?: number;
  /** Explicit item counts; phase runs do not imply item outcomes. */
  outcomes?: ProgressOutcomes | null;
};

const percentage = (work?: ProgressWork | null) => work?.total != null && work.total > 0
  ? Math.round(Math.min(100, Math.max(0, 100 * work.completed / work.total))) : undefined;

const workDescription = (work: ProgressWork) => work.total != null && work.total > 0
  ? `${work.completed.toLocaleString()} of ${work.total.toLocaleString()} ${work.unit} · ${percentage(work)}%`
  : `${work.completed.toLocaleString()} ${work.unit} completed`;

/** A costing's progress as the server reports it (`/progress/{id}/details`), or null for anything else. */
export const parseCostingProgress = (value: unknown): CostingProgress | null =>
  value && typeof value === "object" && typeof (value as CostingProgress).currentLabel === "string" && Array.isArray((value as CostingProgress).stages)
    ? value as CostingProgress : null;

const readProgress = (anchor: Element | null): CostingProgress | null => {
  try {
    return parseCostingProgress(JSON.parse(anchor?.getAttribute(COSTING_PROGRESS_ATTRIBUTE) ?? "null"));
  } catch { return null; }
};

/** Shows observed server stages; stages may run again for another rate model or database. */
export function OtsCostingProgressContent({progress}: {progress: CostingProgress}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (progress.state !== "RUNNING" && progress.updatedAt == null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [progress.state, progress.updatedAt]);
  const elapsedUntil = progress.state === "RUNNING" ? now : progress.updatedAt ?? now;
  const elapsed = progress.startedAt == null ? undefined : Math.max(0, Math.floor((elapsedUntil - progress.startedAt) / 1000));
  const currentStage = progress.stages.find(stage => stage.status === "RUNNING"
    && (progress.currentStageId ? stage.id === progress.currentStageId : stage.label === progress.currentLabel));
  const work = progress.work ?? currentStage?.work;
  const workLabel = progress.work ? "Overall shift costing progress" : `${currentStage?.label ?? "Shift costing"} progress`;
  const workText = work ? workDescription(work) : progress.currentLabel;
  const updatedSecondsAgo = progress.updatedAt == null ? undefined : Math.max(0, Math.floor((now - progress.updatedAt) / 1000));

  return <div className="w-60 space-y-2">
    <div className="flex items-center justify-between gap-4">
      <strong>Money Squish</strong>
      {elapsed !== undefined && <span className="tabular-nums text-[var(--ots-text-muted)]">{elapsed}s</span>}
    </div>
    <div role="status" aria-live="polite">{progress.currentLabel}</div>
    {progress.state === "RUNNING" && <>
      {work && <div className="text-[var(--ots-text-muted)] tabular-nums">{progress.work ? "Overall · " : ""}{workText}</div>}
      <OtsProgressBar label={workLabel} size="compact" value={work?.completed} max={work?.total ?? undefined} valueText={workText}/>
    </>}
    <ul className="space-y-1" aria-label="Costing stages">
      {progress.stages.map(stage => <li key={stage.id} data-stage-status={stage.status}
        className={["flex items-center gap-2", stage.status === "PENDING" || stage.status === "SKIPPED" ? "text-[var(--ots-text-muted)]" : ""].join(" ")}>
        <span aria-hidden="true" className={stage.status === "FAILED" ? "text-[var(--ots-error)]" : stage.status === "COMPLETE" ? "text-[var(--ots-success)]" : ""}>
          {stage.status === "COMPLETE" ? "✓" : stage.status === "RUNNING" ? "↻" : stage.status === "FAILED" ? "!" : "·"}
        </span>
        <span className="flex-1">{stage.label}</span>
        <span className="text-[10px] text-[var(--ots-text-muted)]">
          {stage.status === "RUNNING" ? percentage(stage.work) !== undefined ? `${percentage(stage.work)}%`
            : stage.completedRuns > 0 ? `${stage.completedRuns} done` : "Running"
            : stage.status === "SKIPPED" ? "Not needed"
            : stage.status === "COMPLETE" ? stage.completedRuns > 1 ? `${stage.completedRuns} runs` : "Done"
            : stage.status === "FAILED" ? "Failed" : "Pending"}
        </span>
      </li>)}
    </ul>
    {progress.outcomes && <dl aria-label="Item outcomes" className="grid grid-cols-3 gap-2 tabular-nums">
      <div><dt className="text-[var(--ots-text-muted)]">Succeeded</dt><dd>{progress.outcomes.succeeded.toLocaleString()}</dd></div>
      <div><dt className="text-[var(--ots-text-muted)]">Failed</dt><dd>{progress.outcomes.failed.toLocaleString()}</dd></div>
      <div><dt className="text-[var(--ots-text-muted)]">Skipped</dt><dd>{progress.outcomes.skipped.toLocaleString()}</dd></div>
    </dl>}
    {updatedSecondsAgo !== undefined && <div className="text-[var(--ots-text-muted)]">
      <time dateTime={new Date(progress.updatedAt!).toISOString()}>
        {updatedSecondsAgo === 0 ? "Updated just now" : `Updated ${updatedSecondsAgo}s ago`}
      </time>
    </div>}
  </div>;
}

export type OtsCostingProgressHostProps = {brand?: OtsBrand; colorScheme?: OtsColorSchemePreference};

/** One shared, themed tooltip for GWT money buttons. Attribute updates preserve the live anchor. */
export function OtsCostingProgressHost({brand, colorScheme}: OtsCostingProgressHostProps) {
  const id = useId();
  const [anchor, setAnchor] = useState<Element | null>(null);
  const [eligibleAnchor, setEligibleAnchor] = useState<Element | null>(null);
  const [progress, setProgress] = useState<CostingProgress | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLSpanElement | null>(null);
  // GWT records the client start time, so server clock skew and grid redraws cannot reset the delay.
  const startedAt = useMemo(() => Number(anchor?.getAttribute(COSTING_STARTED_AT_ATTRIBUTE))
    || readProgress(anchor)?.startedAt || Date.now(), [anchor]);
  const state = progress?.state;
  const visible = anchor?.isConnected && eligibleAnchor === anchor && progress !== null;

  useEffect(() => {
    if (!anchor || !state) {
      setEligibleAnchor(null);
      return;
    }
    // A quick terminal response must never open a pending tooltip. An already visible one can show its outcome.
    if (state !== "RUNNING") {
      setEligibleAnchor(current => current === anchor ? current : null);
      return;
    }
    const remaining = COSTING_TOOLTIP_DELAY_MS - (Date.now() - startedAt);
    if (remaining <= 0) {
      setEligibleAnchor(anchor);
      return;
    }
    setEligibleAnchor(null);
    const timer = window.setTimeout(() => {
      if (anchor.isConnected && readProgress(anchor)?.state === "RUNNING") setEligibleAnchor(anchor);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [anchor, state, startedAt]);

  useEffect(() => {
    const show = (event: Event) => {
      const target = event.target instanceof Element ? event.target.closest(`[${COSTING_PROGRESS_ATTRIBUTE}]`) : null;
      setAnchor(target);
      setProgress(readProgress(target));
    };
    const hide = (event: Event) => {
      const next = (event as MouseEvent | FocusEvent).relatedTarget;
      setAnchor(current => current && next instanceof Node && current.contains(next) ? current : null);
    };
    const dismiss = (event: KeyboardEvent) => { if (event.key === "Escape") setAnchor(null); };
    document.addEventListener("mouseover", show, true);
    document.addEventListener("focusin", show, true);
    document.addEventListener("mouseout", hide, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("keydown", dismiss);
    return () => {
      document.removeEventListener("mouseover", show, true);
      document.removeEventListener("focusin", show, true);
      document.removeEventListener("mouseout", hide, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("keydown", dismiss);
    };
  }, []);

  useEffect(() => {
    if (!anchor) return;
    const observer = new MutationObserver(mutations => {
      if (!anchor.isConnected) setAnchor(null);
      else if (mutations.some(mutation => mutation.type === "attributes" && mutation.target === anchor))
        setProgress(readProgress(anchor));
    });
    observer.observe(anchor, {attributes: true, attributeFilter: [COSTING_PROGRESS_ATTRIBUTE]});
    observer.observe(document.body, {childList: true, subtree: true});
    return () => {
      observer.disconnect();
    };
  }, [anchor]);

  useEffect(() => {
    if (!anchor || !visible) return;
    const describedBy = anchor.getAttribute("aria-describedby");
    anchor.setAttribute("aria-describedby", [describedBy, id].filter(Boolean).join(" "));
    return () => {
      if (describedBy) anchor.setAttribute("aria-describedby", describedBy);
      else anchor.removeAttribute("aria-describedby");
    };
  }, [anchor, id, visible]);

  return <span ref={setPortalTarget} data-ots-costing-progress-host="">
    {visible && anchor && progress && portalTarget && <OtsTooltipPanel id={id} anchor={anchor}
      content={<OtsCostingProgressContent progress={progress}/>} density="default"
      brand={brand} colorScheme={colorScheme} portalTarget={portalTarget}/>}
  </span>;
}
